import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import { withStyles } from "@mui/styles";
import PropTypes from "prop-types";
import Dropzone from "../DropZone";
import dropZoneDialogStyle from "./styles/dropZoneStyle";
import {
  Avatar,
  Card,
  CardActions,
  CardContent,
  CardHeader,
} from "@mui/material";

// Split props related to DropzoneDialog from DropzoneArea ones
const splitDropzoneDialogProps = (allProps) => {
  const {
    avatar,
    cancelButtonText,
    dialogProps,
    dialogTitle,
    fullWidth,
    maxWidth,
    onClose,
    onSave,
    open,
    submitButtonText,
    ...otherProps
  } = allProps;

  const { classes, ...dropzoneAreaProps } = otherProps;

  return [
    {
      avatar,
      cancelButtonText,
      dialogProps,
      dialogTitle,
      fullWidth,
      maxWidth,
      onClose,
      onSave,
      open,
      submitButtonText,
    },
    dropzoneAreaProps,
    classes,
  ];
};

/**
 * This component provides the DropzoneArea inside of a Material-UI Dialog.
 *
 * It supports all the Props and Methods from `DropzoneArea`.
 */
class DropzoneDialog extends React.PureComponent {
  static defaultProps = {
    ...Dropzone.defaultProps,
    open: false,
    dialogTitle: "Upload File",
    dialogProps: {},
    fullWidth: true,
    maxWidth: "md",
    cancelButtonText: "Cancel",
    submitButtonText: "Submit",
    showPreviews: false,
    showPreviewsInDropzone: true,
    showFileNamesInPreview: true,
  };

  static propTypes = {
    ...Dropzone.propTypes,
    /** Sets whether the dialog is open or closed. */
    open: PropTypes.bool,
    /** The Dialog title. */
    dialogTitle: PropTypes.string,
    /**
     * Props to pass to the Material-UI Dialog components.
     * @see See [Material-UI Dialog](https://material-ui.com/api/dialog/#props) for available values.
     */
    dialogProps: PropTypes.object,
    /**
     * If `true`, the dialog stretches to `maxWidth`.<br/>
     * Notice that the dialog width grow is limited by the default margin.
     */
    fullWidth: PropTypes.bool,
    /**
     * Determine the max-width of the dialog. The dialog width grows with the size of the screen.<br/>
     * Set to `false` to disable `maxWidth`.
     */
    maxWidth: PropTypes.string,
    /** Cancel button text in dialog. */
    cancelButtonText: PropTypes.string,
    /** Submit button text in dialog. */
    submitButtonText: PropTypes.string,
    /**
     * Fired when the modal is closed
     *
     * @param {SyntheticEvent} event The react `SyntheticEvent`
     */
    onClose: PropTypes.func,
    /**
     * Fired when the user clicks the Submit button.
     *
     * @param {File[]} files All the files currently inside the Dropzone.
     */
    onSave: PropTypes.func,
    /**
     * Shows previews **BELOW** the dropzone.<br/>
     * **Note:** By default previews show up under in the Dialog and inside in the standalone.
     */
    showPreviews: PropTypes.bool,
    /** Shows preview **INSIDE** the dropzone area. */
    showPreviewsInDropzone: PropTypes.bool,
    /** Shows file name under the image. */
    showFileNamesInPreview: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      files: [],
    };
  }

  handleClose = (event) => {
    const { onClose } = this.props;
    // Notify onClose
    if (onClose) {
      onClose(event);
    }
  };

  handleChange = (files) => {
    const { onChange } = this.props;

    this.setState({
      files,
    });

    if (onChange) {
      onChange(files);
    }
  };

  handleSave = () => {
    const { onSave } = this.props;
    const { files } = this.state;

    if (onSave) {
      onSave(files);
    }
  };

  render() {
    const [dropzoneDialogProps, dropzoneAreaProps] = splitDropzoneDialogProps(
      this.props
    );
    const {
      avatar,
      cancelButtonText,
      dialogProps,
      dialogTitle,
      fullWidth,
      maxWidth,
      open,
      submitButtonText,
    } = dropzoneDialogProps;
    const { files } = this.state;

    // Submit button state
    const submitDisabled = files.length === 0;

    return (
      <Dialog
        {...dialogProps}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        onClose={this.handleClose}
        open={open}
      >
        <Card>
          <CardHeader
            title={dialogTitle}
            avatar={avatar && <Avatar src={avatar} variant="square" />}
          />
          <CardContent>
            <Dropzone {...dropzoneAreaProps} onChange={this.handleChange} />
          </CardContent>
          <CardActions>
            <Button color="primary" onClick={this.handleClose}>
              {cancelButtonText}
            </Button>

            <Button
              color="primary"
              disabled={submitDisabled}
              onClick={this.handleSave}
            >
              {submitButtonText}
            </Button>
          </CardActions>
        </Card>
      </Dialog>
    );
  }
}

export default withStyles(dropZoneDialogStyle)(DropzoneDialog);
